// pages/PaymentsPage.tsx
import { useState, useMemo } from "react";
import { Search, Filter, X, Eye, CreditCard, CheckCircle, XCircle, Receipt, ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import PageLayout from "../../../layouts/PageListLayout";
import FlexContainer from "../../../components/ui/FlexContainer";
import PaymentsList from "../../../components/features/payments/PaymentsList";
import DeleteConfirmationModal from "../../../components/ui/DeleteConfirmationModal";
import MenuModal from "../../../components/ui/MenuModal";
import { alertSuccess, alertError } from "../../../helpers/alertError";
import { Payment } from "../../../types/Payment";
import { useAuth } from "../../../hooks/auth/useAuth";
import { usePayments } from "../../../hooks/payments/usePayments";

export default function PaymentsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role;
  
  const { 
    payments, 
    deletePayment, 
    updatePaymentStatus,
    total,
    pages,
    currentPage,
    goToPage,
    nextPage,
    prevPage,
    loading 
  } = usePayments();
  
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>("");
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [paymentToDelete, setPaymentToDelete] = useState<Payment | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const filteredPayments = useMemo(() => {
    let result = payments;

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      result = result.filter(p =>
        p.reference?.toLowerCase().includes(searchLower) ||
        p.transactionId?.toLowerCase().includes(searchLower) ||
        p.customerName?.toLowerCase().includes(searchLower) ||
        p.customerEmail?.toLowerCase().includes(searchLower)
      );
    }

    if (selectedStatus) {
      result = result.filter(p => p.status === selectedStatus);
    }

    if (selectedPaymentMethod) {
      result = result.filter(p => p.paymentMethod === selectedPaymentMethod);
    }

    return result;
  }, [payments, searchTerm, selectedStatus, selectedPaymentMethod]);

  const stats = useMemo(() => ({
    total: payments.length,
    pending: payments.filter(p => p.status === "pending").length,
    approved: payments.filter(p => p.status === "approved").length,
    canceled: payments.filter(p => p.status === "canceled").length,
    totalAmount: payments.filter(p => p.status === "approved").reduce((sum, p) => sum + p.amount, 0)
  }), [payments]);

  const handleDelete = async () => {
    if (!paymentToDelete?.id) return;
    setIsDeleting(true);
    try {
      await deletePayment(paymentToDelete.id);
      setPaymentToDelete(null);
      alertSuccess("Paiement supprimé avec succès");
    } catch (err) {
      alertError("Erreur lors de la suppression");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleUpdateStatus = async (paymentId: string, status: string) => {
    try {
      await updatePaymentStatus(paymentId, status);
      const statusMessages = {
        approved: "Paiement approuvé",
        canceled: "Paiement annulé"
      };
      alertSuccess(statusMessages[status as keyof typeof statusMessages] || "Statut mis à jour");
    } catch (err) {
      alertError("Erreur lors de la mise à jour");
    }
  };

  const clearFilters = () => {
    setSelectedStatus("");
    setSelectedPaymentMethod("");
    setSearchTerm("");
  };

  const hasActiveFilters = selectedStatus || selectedPaymentMethod || searchTerm;

  const statusOptions = [
    { value: "pending", label: "En attente" },
    { value: "approved", label: "Approuvé" },
    { value: "canceled", label: "Annulé" }
  ];

  const paymentMethodOptions = [
    { value: "orange_money", label: "Orange Money" },
    { value: "mtn_money", label: "MTN Money" },
    { value: "moov_money", label: "Moov Money" },
    { value: "bank_transfer", label: "Virement bancaire" },
    { value: "card", label: "Carte bancaire" },
    { value: "cash", label: "Espèces" }
  ];

  // Fonction pour générer les numéros de page
  const getPageNumbers = () => {
    if (!pages || pages <= 1) return [];
    const pageNumbers = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(pages, start + maxVisible - 1);
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }
    for (let i = start; i <= end; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  return (
    <PageLayout
      title="Gestion des paiements"
      description={`${filteredPayments.length} paiement${filteredPayments.length > 1 ? 's' : ''} affiché${filteredPayments.length > 1 ? 's' : ''}`}
      actions={
        <FlexContainer className="animate-fade-in-up w-full">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 rounded-xl transition-all ${showFilters ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            <Filter size={18} /> Filtrer
            {hasActiveFilters && <span className="ml-1 w-2 h-2 bg-red-500 rounded-full" />}
          </button>
        </FlexContainer>
      }
    >
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4">
          <p className="text-xs font-semibold text-blue-700/70">Total</p>
          <p className="text-2xl font-bold text-blue-700">{stats.total}</p>
        </div>
        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-4">
          <p className="text-xs font-semibold text-yellow-700/70">En attente</p>
          <p className="text-2xl font-bold text-yellow-700">{stats.pending}</p>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4">
          <p className="text-xs font-semibold text-green-700/70">Approuvés</p>
          <p className="text-2xl font-bold text-green-700">{stats.approved}</p>
        </div>
        <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-4">
          <p className="text-xs font-semibold text-red-700/70">Annulés</p>
          <p className="text-2xl font-bold text-red-700">{stats.canceled}</p>
        </div>
        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-4">
          <p className="text-xs font-semibold text-emerald-700/70">Montant total</p>
          <p className="text-lg font-bold text-emerald-700">{stats.totalAmount.toLocaleString('fr-FR')} FCFA</p>
        </div>
      </div>

      {/* Recherche */}
      <div className="relative mb-4">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Rechercher par référence, transaction, client ou email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
        />
      </div>

      {/* Filtres */}
      {showFilters && (
        <div className="bg-gray-50 rounded-xl p-4 mb-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-gray-700">Filtres</h3>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1">
                <X size={14} /> Effacer tout
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="">Tous les statuts</option>
                {statusOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Méthode de paiement</label>
              <select
                value={selectedPaymentMethod}
                onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="">Toutes les méthodes</option>
                {paymentMethodOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Liste */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : (
        <>
          <PaymentsList
            payments={filteredPayments}
            selectable={role === "admin"}
            onAction={(p) => setSelectedPayment(p)}
            selectActions={role === "admin" ? [{
              label: "Supprimer",
              onClick: (selected) => setPaymentToDelete(selected[0]),
              className: "bg-red-600 text-white"
            }] : undefined}
          />

          {/* Pagination */}
          {pages > 1 && (
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-gray-200 pt-4">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>Page {currentPage} sur {pages}</span>
                <span className="text-gray-400">•</span>
                <span>{total} paiement{total > 1 ? 's' : ''}</span>
              </div>
              
              <div className="flex items-center gap-1">
                <button
                  onClick={prevPage}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft size={18} />
                </button>
                
                {getPageNumbers().map(page => (
                  <button
                    key={page}
                    onClick={() => goToPage(page)}
                    className={`w-10 h-10 rounded-lg transition-colors ${
                      currentPage === page
                        ? 'bg-primary text-white font-medium'
                        : 'hover:bg-gray-50 border border-gray-200'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                
                <button
                  onClick={nextPage}
                  disabled={currentPage === pages}
                  className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Menu modal */}
      {selectedPayment && (
        <MenuModal
          title={`Paiement ${selectedPayment.reference}`}
          isOpen={!!selectedPayment}
          onClose={() => setSelectedPayment(null)}
          icon={<CreditCard className="text-primary" size={20} />}
          menu={[
            { 
              label: "Voir détails", 
              icon: Eye, 
              onClick: () => navigate(`/${role}/payments/details?id=${selectedPayment.id}`) 
            },
            ...(selectedPayment.receiptText ? [
              { 
                label: "Voir le reçu", 
                icon: Receipt, 
                onClick: () => {
                  alertSuccess("Reçu disponible");
                } 
              }
            ] : []),
            ...((role === "admin") ? [
              ...(selectedPayment.status === "pending" ?
                [{ 
                  label: "Approuver le paiement", 
                  icon: CheckCircle, 
                  onClick: () => handleUpdateStatus(selectedPayment.id, "approved") 
                }] : []),
              ...(selectedPayment.status !== "canceled" && selectedPayment.status !== "approved" ?
                [{ 
                  label: "Annuler le paiement", 
                  icon: XCircle, 
                  onClick: () => handleUpdateStatus(selectedPayment.id, "canceled") 
                }] : [])
            ] : []),
            { 
              label: "Supprimer", 
              icon: CreditCard, 
              onClick: () => { 
                setPaymentToDelete(selectedPayment); 
                setSelectedPayment(null); 
              } 
            }
          ]}
        />
      )}

      {/* Modal suppression */}
      <DeleteConfirmationModal
        isOpen={!!paymentToDelete}
        onClose={() => setPaymentToDelete(null)}
        onConfirm={handleDelete}
        title="Supprimer le paiement"
        message={`Supprimer le paiement "${paymentToDelete?.reference}" ?`}
        confirmText={isDeleting ? "Suppression..." : "Supprimer"}
      />
    </PageLayout>
  );
}